import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const wave = keyframes`
  0%, 100% { transform: scaleY(0.2); }
  25% { transform: scaleY(0.85); }
  50% { transform: scaleY(0.4); }
  75% { transform: scaleY(1); }
`;

const WaveformContainer = styled.div<{ $isPlaying: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  pointer-events: none;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 1 : 0.25)};
  transition: opacity 0.8s ease;
`;

const WaveformBar = styled.div<{ $delay: number; $duration: number; $isPlaying: boolean; $height: number }>`
  flex: 1;
  max-width: 10px;
  min-width: 4px;
  height: ${({ $height }) => $height}%;
  background: linear-gradient(
    to bottom,
    rgba(0, 221, 163, 0.05) 0%,
    rgba(0, 221, 163, 0.9) 50%,
    rgba(0, 221, 163, 0.05) 100%
  );
  border-radius: 5px;
  transform-origin: center;
  animation: ${wave} ${({ $duration }) => $duration}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  animation-play-state: ${({ $isPlaying }) => ($isPlaying ? 'running' : 'paused')};
  box-shadow: 0 0 12px rgba(0, 221, 163, 0.4), 0 0 4px rgba(0, 221, 163, 0.2);
`;

type WaveformProps = {
  isPlaying: boolean;
  barCount?: number;
};

export const Waveform: React.FC<WaveformProps> = ({ isPlaying, barCount = 32 }) => {
  const bars = useMemo(() =>
    Array.from({ length: barCount }, (_, i) => {
      const centerDistance = Math.abs(i - barCount / 2);
      const normalizedDist = centerDistance / (barCount / 2);
      const pseudoRandom = ((i * 7919) % 1000) / 1000;
      // Bars are taller in the center, shorter at edges (bell curve shape)
      const heightPct = 40 + (1 - normalizedDist * normalizedDist) * 60;
      return {
        delay: centerDistance * 0.025,
        duration: 0.7 + pseudoRandom * 0.4,
        height: heightPct,
      };
    }),
    [barCount]
  );

  return (
    <WaveformContainer $isPlaying={isPlaying}>
      {bars.map((bar, i) => (
        <WaveformBar
          key={`wave-${i}`}
          $delay={bar.delay}
          $duration={bar.duration}
          $isPlaying={isPlaying}
          $height={bar.height}
        />
      ))}
    </WaveformContainer>
  );
};
